import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  Box,
  Autocomplete,
  TextField,
  Stack,
  Alert,
  Checkbox,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productEquivalenciesApi, productEquivalencyMembershipsApi, productsApi } from '../../api/services';
import type { ProductEquivalency, ProductEquivalencyMembership, Product } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';

interface MembershipManagerProps {
  open: boolean;
  onClose: () => void;
  group: ProductEquivalency;
}

export const MembershipManager: React.FC<MembershipManagerProps> = ({
  open,
  onClose,
  group,
}) => {
  const queryClient = useQueryClient();
  const [selectedSku, setSelectedSku] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { data: members = [] } = useQuery({
    queryKey: ['product_equivalency_members', group.id],
    queryFn: () => productEquivalenciesApi.getMembers(group.id),
    enabled: open,
    select: (data) => [...data].sort((a, b) => a.id - b.id),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  // Filter out products already in this equivalency
  const memberSkus = new Set(members.map(m => m.sku));
  const availableProducts = products.filter(p => !memberSkus.has(p.sku));

  const addMutation = useMutation({
    mutationFn: (data: { product_equivalency_id: number; sku: string }) =>
      productEquivalencyMembershipsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_members', group.id] });
      queryClient.invalidateQueries({ queryKey: ['product_equivalencies'] });
      setSelectedSku(null);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(extractApiError(err, 'Failed to add member'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { hide_when_zero: boolean } }) =>
      productEquivalencyMembershipsApi.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['product_equivalency_members', group.id] });
      const previous = queryClient.getQueryData<ProductEquivalencyMembership[]>(['product_equivalency_members', group.id]);
      queryClient.setQueryData<ProductEquivalencyMembership[]>(
        ['product_equivalency_members', group.id],
        (old) => old?.map((m) => (m.id === id ? { ...m, ...data } : m)) ?? [],
      );
      setError(null);
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['product_equivalency_members', group.id], context.previous);
      }
      setError(extractApiError(err, 'Failed to update member'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: productEquivalencyMembershipsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_members', group.id] });
      queryClient.invalidateQueries({ queryKey: ['product_equivalencies'] });
      setError(null);
    },
    onError: (err: unknown) => {
      setError(extractApiError(err, 'Failed to remove member'));
    },
  });

  const handleAdd = () => {
    if (!selectedSku) return;
    addMutation.mutate({
      product_equivalency_id: group.id,
      sku: selectedSku,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Members of {group.name}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Category: {group.product_equivalency_category?.name}{group.hexa_hq_sku ? ` | SKU: ${group.hexa_hq_sku}` : ''}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Add member form */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Autocomplete
            value={availableProducts.find(p => p.sku === selectedSku) || null}
            onChange={(_event, newValue: Product | null) => setSelectedSku(newValue?.sku || null)}
            options={availableProducts}
            getOptionLabel={(option) => `${option.sku} - ${option.name}`}
            renderInput={(params) => <TextField {...params} label="Add Product" size="small" />}
            sx={{ flex: 1 }}
            size="small"
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={!selectedSku || addMutation.isPending}
          >
            Add
          </Button>
        </Stack>

        {/* Member list */}
        <List dense>
          {members.map((membership) => {
            const isHexa = membership.product?.manufacturer?.name === 'Hexa';
            return (
            <ListItem key={membership.id}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {membership.sku}
                    {isHexa && (
                      <Chip
                        icon={<StarIcon />}
                        label="Hexa"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={membership.product?.name || ''}
              />
              <ListItemSecondaryAction>
                <Tooltip title={membership.hide_when_zero ? 'Hidden from digest when qty is 0' : 'Shown in digest even when qty is 0'}>
                  <Checkbox
                    size="small"
                    checked={!!membership.hide_when_zero}
                    onChange={(e) => updateMutation.mutate({ id: membership.id, data: { hide_when_zero: e.target.checked } })}
                    icon={<VisibilityOffIcon sx={{ fontSize: 20, color: 'action.disabled' }} />}
                    checkedIcon={<VisibilityOffIcon sx={{ fontSize: 20, color: 'warning.main' }} />}
                  />
                </Tooltip>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => removeMutation.mutate(membership.id)}
                  disabled={removeMutation.isPending}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
          })}
          {members.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No members yet"
                secondary="Add products to this equivalency using the form above"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
